import { useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import {
  GfFlyerItemCategory,
  GfMerchant,
  GfStore,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../locales/messages";
import {
  AutocompleteOption,
  CustomAutocompleteMultiSelect,
} from "../../components/CustomAutocomplete";
import { useMerchants } from "../../../hooks/useMerchants";
import { useStores } from "../../../hooks/useStores";
import { useFlyerItemCategories } from "../../../hooks/useFlyerItemCategories";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { DEFAULT_ANALYTICS_FILTERS } from "../../../utils/analytics/types";
import { analyticsTheme, cardSx, BRAND } from "./theme";
import { AnalyticsFilters } from "./types";

interface AnalyticsFilterBarProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
  hideMerchantAndStoreFilters?: boolean;
}

const ORANGE = BRAND;

const datePickerSx = {
  width: "100%",
  backgroundColor: analyticsTheme.cardBg,
  "& .MuiOutlinedInput-root": {
    borderRadius: analyticsTheme.radius.sm,
    "& fieldset": { borderColor: analyticsTheme.borderStrong },
    "&:hover fieldset": { borderColor: analyticsTheme.text.muted },
    "&.Mui-focused fieldset": { borderColor: ORANGE },
  },
};

const fieldLabelSx = {
  pb: "4px",
  fontSize: 16,
  lineHeight: 1.5,
  color: analyticsTheme.text.primary,
};

const datePickerDaySx = {
  "&.Mui-selected": {
    backgroundColor: `${ORANGE} !important`,
    "&:hover": { backgroundColor: "#d97320 !important" },
  },
};

export const AnalyticsFilterBar = ({
  filters,
  onFiltersChange,
  hideMerchantAndStoreFilters = false,
}: AnalyticsFilterBarProps) => {
  const { t } = useTranslation();
  const { merchantsList, isMerchantOptionLoading, handleOnMerchantSearch } =
    useMerchants();
  const { storesList, isStoreOptionLoading, handleOnStoreSearch } = useStores();
  const { flyerItemCategories } = useFlyerItemCategories();

  const fromDate = useMemo(() => {
    if (filters.dateFrom && filters.dateFrom !== "-30d") {
      return dayjs(filters.dateFrom);
    }
    return dayjs().subtract(30, "day");
  }, [filters.dateFrom]);

  const toDate = useMemo(
    () => (filters.dateTo ? dayjs(filters.dateTo) : dayjs()),
    [filters.dateTo],
  );

  const selectedMerchant = useMemo(
    () => merchantsList?.find((m) => m.id === filters.merchantId),
    [merchantsList, filters.merchantId],
  );

  const filteredStores = useMemo(() => {
    if (!storesList) return [];
    if (!selectedMerchant) return storesList;
    return storesList.filter(
      (store) => store.merchant?.id === selectedMerchant.id,
    );
  }, [storesList, selectedMerchant]);

  const selectedStore = useMemo(
    () => filteredStores.find((s) => s.id === filters.storeId),
    [filteredStores, filters.storeId],
  );

  const selectedCategory = useMemo(
    () => flyerItemCategories.find((c) => c.id === filters.categoryId),
    [flyerItemCategories, filters.categoryId],
  );

  const merchantOptions: AutocompleteOption<GfMerchant>[] = useMemo(
    () =>
      (merchantsList ?? []).map((merchant) => ({
        value: merchant,
        label: merchant.name,
      })),
    [merchantsList],
  );

  const storeOptions: AutocompleteOption<GfStore>[] = useMemo(
    () =>
      filteredStores.map((store) => ({
        value: store,
        label: makeStoreDisplayName(store),
      })),
    [filteredStores],
  );

  const categoryOptions: AutocompleteOption<GfFlyerItemCategory>[] = useMemo(
    () =>
      flyerItemCategories.map((category) => ({
        value: category,
        label: category.name,
      })),
    [flyerItemCategories],
  );

  const handleMerchantChange = (merchants: GfMerchant[] | undefined) => {
    const merchant = merchants?.[0];
    const storeStillValid =
      !!filters.storeId &&
      !!merchant &&
      (storesList ?? []).some(
        (store) =>
          store.id === filters.storeId && store.merchant?.id === merchant.id,
      );

    onFiltersChange({
      ...filters,
      merchantId: merchant?.id,
      storeId: storeStillValid ? filters.storeId : undefined,
    });
  };

  const handleStoreChange = (stores: GfStore[] | undefined) => {
    const store = stores?.[0];
    onFiltersChange({
      ...filters,
      storeId: store?.id,
    });
  };

  const handleCategoryChange = (
    categories: GfFlyerItemCategory[] | undefined,
  ) => {
    const category = categories?.[0];
    onFiltersChange({
      ...filters,
      categoryId: category?.id,
    });
  };

  const handleFromDateChange = (date: Dayjs | null) => {
    if (!date) {
      onFiltersChange({
        ...filters,
        dateFrom: "-30d",
      });
      return;
    }

    const defaultFrom = dayjs().subtract(30, "day").startOf("day");
    const isDefaultRelative =
      date.startOf("day").isSame(defaultFrom) && !filters.dateTo;

    onFiltersChange({
      ...filters,
      dateFrom: isDefaultRelative ? "-30d" : date.format("YYYY-MM-DD"),
    });
  };

  const handleToDateChange = (date: Dayjs | null) => {
    onFiltersChange({
      ...filters,
      dateTo: date ? date.format("YYYY-MM-DD") : undefined,
    });
  };

  const hasActiveFilters =
    !!filters.merchantId ||
    !!filters.storeId ||
    !!filters.categoryId ||
    (!!filters.dateFrom && filters.dateFrom !== "-30d") ||
    !!filters.dateTo;

  const handleReset = () => onFiltersChange({ ...DEFAULT_ANALYTICS_FILTERS });

  return (
    <Box
      sx={{
        ...cardSx,
        p: { xs: 2, sm: 2.5 },
      }}
      data-testid="analytics-filter-bar"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TuneRoundedIcon
            sx={{ fontSize: 18, color: analyticsTheme.text.secondary }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: analyticsTheme.text.secondary,
            }}
          >
            {t(messages.Filters())}
          </Typography>
        </Box>

        <Button
          onClick={handleReset}
          disabled={!hasActiveFilters}
          startIcon={<RestartAltRoundedIcon />}
          data-testid="analytics-reset-filters"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: analyticsTheme.text.secondary,
            borderRadius: analyticsTheme.radius.pill,
            px: 1.5,
            "&:hover": {
              backgroundColor: analyticsTheme.brandSoft,
              color: BRAND,
            },
          }}
        >
          {t(messages.Reset())}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          alignItems: "start",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: hideMerchantAndStoreFilters
              ? "repeat(3, minmax(0, 1fr))"
              : "repeat(5, minmax(0, 1fr))",
          },
        }}
      >
        {!hideMerchantAndStoreFilters && (
          <>
            <CustomAutocompleteMultiSelect
              title={t(messages.Merchant()) as string}
              optionsList={merchantOptions}
              selectedValues={
                selectedMerchant
                  ? [{ value: selectedMerchant, label: selectedMerchant.name }]
                  : []
              }
              multiSelect={false}
              handleOnChange={handleMerchantChange}
              isLoading={isMerchantOptionLoading}
              handleOnSearch={handleOnMerchantSearch}
              dataTestId="analytics-merchant-filter"
              WrapperCss="width: 100%;"
            />

            <CustomAutocompleteMultiSelect
              title={t(messages.Stores()) as string}
              optionsList={storeOptions}
              selectedValues={
                selectedStore
                  ? [
                      {
                        value: selectedStore,
                        label: makeStoreDisplayName(selectedStore),
                      },
                    ]
                  : []
              }
              multiSelect={false}
              handleOnChange={handleStoreChange}
              isLoading={isStoreOptionLoading}
              handleOnSearch={handleOnStoreSearch}
              dataTestId="analytics-store-filter"
              WrapperCss="width: 100%;"
            />
          </>
        )}

        <CustomAutocompleteMultiSelect
          title={t(messages.Category()) as string}
          optionsList={categoryOptions}
          selectedValues={
            selectedCategory
              ? [{ value: selectedCategory, label: selectedCategory.name }]
              : []
          }
          multiSelect={false}
          handleOnChange={handleCategoryChange}
          dataTestId="analytics-category-filter"
          WrapperCss="width: 100%;"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box>
            <Typography sx={fieldLabelSx}>
              {t(messages.Start_Date())}
            </Typography>
            <DatePicker
              value={fromDate}
              onChange={handleFromDateChange}
              maxDate={toDate ?? undefined}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: datePickerSx,
                  inputProps: { "aria-label": t(messages.Start_Date()) },
                },
                day: { sx: datePickerDaySx },
              }}
              data-testid="analytics-date-from"
            />
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>{t(messages.End_Date())}</Typography>
            <DatePicker
              value={toDate}
              onChange={handleToDateChange}
              minDate={fromDate ?? undefined}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: datePickerSx,
                  inputProps: { "aria-label": t(messages.End_Date()) },
                },
                day: { sx: datePickerDaySx },
              }}
              data-testid="analytics-date-to"
            />
          </Box>
        </LocalizationProvider>
      </Box>
    </Box>
  );
};
